import type { TFunction } from 'i18next';

import { EOnboardingMilestone, EOnboardingTaskType } from '@feathr/blackbox';
import { EOnboardingProgressiveFeatures } from '@feathr/extender/hooks';

export interface ITaskConfig {
  taskType: EOnboardingTaskType;
  title: string;
  description: string;
  actionUrl?: string;
  completedActionUrl: string;
  completedActionText: string;
  optional?: boolean;
  order: number;
  progressiveFeature?: EOnboardingProgressiveFeatures;
  requiresModal?: boolean;
}

export function taskRequiresModal(taskType: EOnboardingTaskType): boolean {
  const modalRequiredTasks = [
    EOnboardingTaskType.SetupSuperPixel,
    EOnboardingTaskType.CreateFirstProject,
    EOnboardingTaskType.BuildFirstGroup,
    EOnboardingTaskType.CreateEmailTemplate,
    EOnboardingTaskType.PublishFirstEmailCampaign,
    EOnboardingTaskType.InviteOtherUsers,
  ];

  return modalRequiredTasks.includes(taskType);
}

export function getMilestoneTasks(milestone: EOnboardingMilestone, t: TFunction): ITaskConfig[] {
  const milestoneTasksMap: Record<EOnboardingMilestone, ITaskConfig[]> = {
    [EOnboardingMilestone.InitialAccountSetup]: [
      {
        taskType: EOnboardingTaskType.SetupBilling,
        title: t('Configure Billing'),
        description: t('Add payment information to your account for campaign billing.'),
        actionUrl: '/settings/billing/configurations/add?onboarding=true',
        completedActionUrl: '/settings/billing/configurations',
        completedActionText: t('Manage billing'),
        order: 1,
      },
      {
        taskType: EOnboardingTaskType.SetupSuperPixel,
        title: t('Set up your Super Pixel'),
        description: t(
          "Install Feathr's tracking code on your website to begin collecting audience data.",
        ),
        completedActionUrl: '/data/pixel',
        completedActionText: t('View Super Pixel'),
        order: 2,
      },
      {
        taskType: EOnboardingTaskType.CreateFirstProject,
        title: t('Create a Project'),
        description: t('Use projects to organize your Feathr campaigns.'),
        completedActionUrl: '/projects',
        completedActionText: t('Manage projects'),
        order: 3,
      },
      {
        taskType: EOnboardingTaskType.AuthenticateDomain,
        title: t('Authenticate your Domain'),
        description: t("Connect your organization's email sending domain to your Feathr account."),
        actionUrl: '/settings/account/domains?onboarding=true',
        completedActionUrl: '/settings/account/domains',
        completedActionText: t('Manage domains'),
        order: 4,
      },
      {
        taskType: EOnboardingTaskType.BuildFirstGroup,
        title: t('Build your first Group'),
        description: t(
          'Groups are segments of your tracked audience that you can target with ads and emails.',
        ),
        completedActionUrl: '/data/explore/all/people',
        completedActionText: t('Manage groups'),
        order: 5,
        progressiveFeature: EOnboardingProgressiveFeatures.SetupSuperPixel,
      },
      {
        taskType: EOnboardingTaskType.InviteOtherUsers,
        title: t('Invite Users'),
        description: t('Add team members to your Feathr account.'),
        completedActionUrl: '/settings/account/users',
        completedActionText: t('Manage users'),
        optional: true,
        order: 6,
      },
    ],
    [EOnboardingMilestone.SingleSendCampaign]: [
      {
        taskType: EOnboardingTaskType.CreateEmailTemplate,
        title: t('Create Email Template'),
        description: t("Use Feathr's drag-and-drop template builder to design your email."),
        completedActionUrl: '/settings/account/templates',
        completedActionText: t('Manage templates'),
        order: 1,
      },
      {
        taskType: EOnboardingTaskType.ImportEmailData,
        title: t('Import Email Data'),
        description: t('Upload your email contact list into Feathr'),
        actionUrl: '/data/imports?onboarding=true',
        completedActionUrl: '/data/imports',
        completedActionText: t('Manage data'),
        order: 2,
      },
      {
        taskType: EOnboardingTaskType.PublishFirstEmailCampaign,
        title: t('Send Email'),
        description: t('Schedule or send your completed email.'),
        completedActionUrl: '/marketing/email',
        completedActionText: t('Manage campaigns'),
        order: 3,
        progressiveFeature: EOnboardingProgressiveFeatures.ImportEmailData,
      },
    ],
    [EOnboardingMilestone.RetargetingCampaign]: [
      {
        taskType: EOnboardingTaskType.PublishFirstAdCampaign,
        title: t('Build Ad Campaign'),
        description: t('Publish your first ad campaign.'),
        completedActionUrl: '/marketing/ads',
        completedActionText: t('Manage campaigns'),
        order: 1,
      },
    ],
  };

  return milestoneTasksMap[milestone] ?? [];
}
